import React, { useEffect, useState, useContext } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  InputAdornment,
  IconButton,
  Grid,
  Paper,
  TextField,
  Typography,
  DialogActions,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import currency from "currency.js";
import AddIcon from "@mui/icons-material/Add";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import { getApplicationFee, getCatalogOptionsByFieldName } from "../../common/util";
import CloseIcon from "@mui/icons-material/Close";
import { UserContext } from "../../context/UserContext";

export default function PricingVariant(props) {
  const { value } = props;

  const onChange = (mode, idx, data, meta) => {
    const arr = [...value];

    if (mode === "add") {
      arr.push(data);
      props.onChange(arr);
    } else if (mode === "edit") {
      arr[idx] = data;
      const oldName = meta?.previousName;
      const newName = data?.name;
      if (oldName != null && String(oldName) !== String(newName || "")) {
        props.onChange(arr, {
          remapSizeOverrideFrom: String(oldName),
          remapSizeOverrideTo: String(newName || ""),
        });
      } else {
        props.onChange(arr);
      }
    } else if (mode === "delete") {
      const deletedName = meta?.deletedSizeName ?? value[idx]?.name;
      arr.splice(idx, 1);
      props.onChange(arr, { deletedSizeName: deletedName });
    }
  };

  return (
    <Box>
      <Box>
        <Grid container alignItems="center">
          <Grid item xs={9}>
            <Typography variant="h6">Variant Pricing</Typography>
          </Grid>
          <Grid item xs={3} align="right">
            <Entry
              mode="add"
              value=""
              siblingNames={(value || []).map((v) => v.name)}
              onChange={(mode, data) => onChange(mode, 0, data)}
            />
          </Grid>
        </Grid>
      </Box>
      <Paper elevation={0}>
        <Box px={2} pt={4} pb={2}>
          {value &&
            Object.entries(value).map(([k, v], idx) => {
              return (
                <Row
                  key={k}
                  idx={idx}
                  variant={v}
                  siblingNames={(value || []).filter((_, i) => i !== idx).map((s) => s.name)}
                  onChange={onChange}
                />
              );
            })}
          {value.length === 0 && (
            <Box px={2} py={4} align="center">
              <Typography variant="subtitle1">Add Variant</Typography>
              <Typography variant="body1" color="textSecondary">
                Click + to add one or more variants
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

function Row(props) {
  const { idx, variant, siblingNames } = props;

  const onChange = (mode, data, meta) => {
    props.onChange(mode, idx, data, meta);
  };

  return (
    <Box mb={1}>
      <Box>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={9}>
            <Typography variant="body1">{variant.name}</Typography>
          </Grid>
          <Grid item xs={2} align="right">
            <Typography variant="body2">{currency(variant.pricing.amount).format()}</Typography>
          </Grid>
          <Grid item xs={1} align="right">
            <Entry mode="edit" variant={variant} siblingNames={siblingNames} onChange={onChange} />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

function Entry(props) {
  const { user } = useContext(UserContext);
  const { variant, mode, siblingNames } = props;

  const [open, setOpen] = useState(false);

  const [size, setSize] = useState("");
  const [sizeError, setSizeError] = useState("");

  const [price, setPrice] = useState("");
  const [includeFees, setIncludeFees] = useState("");

  useEffect(() => {
    if (open) {
      setSize(variant?.name);
      setPrice(variant?.pricing?.amount || "");
      setIncludeFees(variant?.pricing?.includeFees || "");
      setSizeError("");
    } else {
      reset();
    }
  }, [open]);

  const submit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const nextName = String(size || "").trim();
    const currentName = String(variant?.name || "").trim();
    const taken = (siblingNames || []).some((n) => String(n || "").trim() === nextName);
    if (taken && nextName !== currentName) {
      setSizeError("A variant with this size name already exists");
      return;
    }

    const next = { name: nextName, pricing: { type: "fixed", amount: currency(price).value } };
    const meta =
      mode === "edit" && currentName !== nextName ? { previousName: variant.name } : undefined;
    props.onChange(mode, next, meta);
    setOpen(false);
    reset();
  };

  const handleDelete = () => {
    props.onChange("delete", undefined, { deletedSizeName: variant?.name });
    setOpen(false);
    reset();
  };

  const reset = () => {
    setSize("");
    setPrice("");
    setSizeError("");
  };

  const SIZE_OPTIONS = getCatalogOptionsByFieldName("size");

  return (
    <span>
      <IconButton size="small" onClick={() => setOpen(true)}>
        {mode === "edit" ? <EditOutlinedIcon /> : <AddIcon />}
      </IconButton>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <form onSubmit={submit}>
          <DialogTitle>
            Variant Details{" "}
            <IconButton
              onClick={() => setOpen(false)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box pt={2}>
              <Grid container spacing={3}>
                <Grid item sm={12} xs={12}>
                  <Autocomplete
                    value={size}
                    onChange={(e, val) => {
                      setSize(val);
                      setSizeError("");
                    }}
                    onInputChange={(e, val) => {
                      setSize(val);
                      setSizeError("");
                    }}
                    options={SIZE_OPTIONS}
                    filterOptions={(options, { inputValue }) => {
                      const normalizedInput = inputValue.toLowerCase();
                      return options.filter(
                        (option) =>
                          typeof option === "string" &&
                          option.toLowerCase().startsWith(normalizedInput)
                      );
                    }}
                    filterSelectedOptions
                    renderInput={(params) => (
                      <TextField
                        variant="outlined"
                        {...params}
                        label="Size"
                        required={true}
                        error={Boolean(sizeError)}
                        helperText={
                          sizeError ||
                          "e.g. 6-inch (6-8 servings) / Single / Set of 12 / Small / Large, etc"
                        }
                      />
                    )}
                    freeSolo
                    blurOnSelect={true}
                  />
                </Grid>
                <Grid item sm={12} xs={12}>
                  <TextField
                    name="price"
                    value={price}
                    variant="outlined"
                    fullWidth
                    id="price"
                    label="Price"
                    onChange={(e) => setPrice(e.target.value)}
                    onWheel={(e) => {
                      e.target && e.target.blur();
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoneyOutlinedIcon />
                        </InputAdornment>
                      ),
                    }}
                    type="number"
                    required
                    helperText={
                      +price > 0 && !user.config?.fees?.model?.includes("cost+")
                        ? `You'll get ${currency(+price - +getApplicationFee(price, user.config?.fees)).format()} after fees`
                        : "Enter final price, including all mandatory fees"
                    }
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            {mode === "edit" && (
              <Button fullWidth onClick={handleDelete} style={{ color: "#e74c3c" }}>
                Remove
              </Button>
            )}
            <Button fullWidth variant="contained" color="primary" type="submit">
              Confirm
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </span>
  );
}
